'use client';

import type { UIEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuthToken } from '@/hooks/useAuthToken';
import { downloadVideoFile } from '@/lib/downloadVideo';
import { Image as ImageIcon, Video as VideoIcon, Copy, Download, ExternalLink, Loader2, Megaphone, ScanLine, Trash2, Volume2, VolumeX, UploadCloud, X } from 'lucide-react';
import Link from 'next/link';
import { useI18n } from '@/contexts/I18nContext';
import { SafeGenerationImage } from '@/components/studio/SafeGenerationImage';
import { HeroGenerator } from '@/components/studio/HeroGenerator';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { BrandSelect } from '@/components/brands/BrandSelect';
import {
  VIDEO_ACCEPT,
  extractVideoMetadata,
  validateVideoFile,
  videoMimeType,
} from '@/lib/utils/video-metadata';

interface Item {
  jobId: string;
  type: 'image' | 'video';
  model: string;
  prompt: string;
  status: 'created' | 'processing' | 'completed' | 'failed';
  outputs: string[];
  s3Url?: string;
  thumbnailUrl?: string | null;
  videoId?: string | null;
  error?: string;
  createdAt: string;
  durationSec?: number;
  aspectRatio?: string;
  /** 'wavespeed' = AI-генерация (поллим статус), 'legacy-video' = загруженное/рендеренное видео. */
  source?: 'wavespeed' | 'legacy-video';
}

type Filter = 'all' | 'image' | 'video';

/**
 * Module-level cache for the user's history feed. Keyed by filter so each
 * tab (`all` / `image` / `video`) is remembered independently and survives
 * navigations (Library ↔ Studio) within the session. Bumped via `refreshKey`
 * when something local changes (new submission, upload, delete).
 */
const historyCache: Partial<Record<string, Item[]>> = {};
const cacheKey = (filter: Filter, brandId: string) => `${filter}:${brandId}`;

/**
 * Convert an `Item.aspectRatio` string like "16:9" / "9:16" / "1:1" into a
 * CSS aspect-ratio value ("16 / 9"). Falls back to a portrait 9 / 16 when
 * the value is missing or unparseable so the masonry layout stays sane.
 */
function parseAspectRatio(ratio?: string): string {
  if (!ratio) return '9 / 16';
  const m = ratio.match(/^\s*(\d+(?:\.\d+)?)\s*[:\/x]\s*(\d+(?:\.\d+)?)\s*$/i);
  if (!m) return '9 / 16';
  const w = parseFloat(m[1]);
  const h = parseFloat(m[2]);
  if (!w || !h) return '9 / 16';
  return `${w} / ${h}`;
}

export function HomeGallery() {
  const { authedFetch } = useAuthToken();
  const { t } = useI18n();
  const [filter, setFilter] = useState<Filter>('all');
  // Agency's brand filter: '' = all, 'none' = without a brand, <id> = one brand.
  const [brandId, setBrandId] = useState('');
  const [items, setItems] = useState<Item[]>(() => historyCache[cacheKey(filter, brandId)] ?? []);
  const [loading, setLoading] = useState(() => !historyCache[cacheKey(filter, brandId)]);
  const [uploadOpen, setUploadOpen] = useState(false);

  // ?upload=1 открывает форму сразу — по этой ссылке приходят с плитки
  // дашборда и со страницы станций.
  //
  // Читаем адрес ПОСЛЕ монтирования, а не в инициализаторе состояния: на
  // сервере window нет, стартовые значения разошлись бы, и React упал бы на
  // гидратации, утащив за собой всю страницу.
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('upload') === '1') {
      setUploadOpen(true);
    }
  }, []);
  const [refreshKey, setRefreshKey] = useState(0);
  const [bottomPrompt, setBottomPrompt] = useState('');
  const [bottomType, setBottomType] = useState<'image' | 'video'>('video');

  // quiet=true — фоновый рефетч (для авто-обновления модерации), без спиннера.
  // Кэш hydration на смену фильтра делается в useEffect ниже, чтобы grid не
  // мигал пустотой при переключении All/Image/Video.
  const loadHistory = useCallback(
    async (quiet = false) => {
      try {
        if (!quiet) setLoading(true);
        const params = new URLSearchParams({ limit: '60' });
        if (filter !== 'all') params.set('type', filter);
        if (brandId) params.set('brandId', brandId);
        const res = await authedFetch(`/api/ai/wavespeed/history?${params}`);
        const json = await res.json();
        if (json.success) {
          historyCache[cacheKey(filter, brandId)] = json.data;
          setItems(json.data);
        }
      } finally {
        if (!quiet) setLoading(false);
      }
    },
    [authedFetch, filter, brandId]
  );

  useEffect(() => {
    const cached = historyCache[cacheKey(filter, brandId)];
    if (cached) {
      setItems(cached);
      setLoading(false);
    }
    // Revalidate in the background — quiet=true when we already had a cache
    // hit so the grid doesn't flash to skeleton state on tab switch.
    void loadHistory(!!cached);
  }, [loadHistory, refreshKey, filter, brandId]);

  // Poll anything still pending so a stale "Queued" card eventually resolves.
  const pending = useMemo(
    () =>
      items
        // Поллим только wavespeed-генерации. Загруженные/рендеренные видео
        // (source 'legacy-video') имеют jobId = id Video, а /status/:id ищет
        // только GenerationJob → 404. Их статус обновляется рефетчем history.
        .filter(
          (it) =>
            it.source === 'wavespeed' &&
            (it.status === 'created' || it.status === 'processing')
        )
        .map((it) => it.jobId),
    [items]
  );

  const pollOnce = useCallback(
    async (jobId: string) => {
      try {
        const res = await authedFetch(`/api/ai/wavespeed/status/${jobId}`);
        const json = await res.json();
        if (json.success) {
          setItems((prev) =>
            prev.map((p) => (p.jobId === jobId ? { ...p, ...json.data } : p))
          );
        }
      } catch {}
    },
    [authedFetch]
  );

  useEffect(() => {
    if (!pending.length) return;
    for (const id of pending) pollOnce(id); // kick one immediately
    const timer = setInterval(() => {
      for (const id of pending) pollOnce(id);
    }, 2500);
    return () => clearInterval(timer);
  }, [pending, pollOnce]);

  const stopNestedScroll = (event: UIEvent) => {
    event.stopPropagation();
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col text-white">
    <section
      data-lenis-prevent
      className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-4 pt-16 sm:px-6 sm:pb-6 md:pt-6"
      // Soft fade at the bottom: the section's overflow-y-auto clip edge
      // otherwise cuts the last visible video row in a sharp horizontal
      // line that reads as a frame on top of the grid.
      style={{
        maskImage:
          'linear-gradient(to bottom, black calc(100% - 56px), transparent)',
        WebkitMaskImage:
          'linear-gradient(to bottom, black calc(100% - 56px), transparent)',
      }}
      onWheelCapture={stopNestedScroll}
      onTouchMoveCapture={stopNestedScroll}
    >
      <div className="mb-4 sm:mb-6">
        <h1 className="text-2xl font-semibold tracking-normal text-white sm:text-3xl">
          {t('studio.myStudio')}
        </h1>
        <p className="mt-1 text-sm text-white/60">
          {t('studio.homeDescription')}
        </p>
      </div>

      {/* Toolbar above the grid: filter pills on the left, Upload on the right. */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1 rounded-full border border-white/10 bg-white/5 p-1 backdrop-blur-sm">
          <FilterChip active={filter === 'all'} onClick={() => setFilter('all')} label={t('studio.all')} />
          <FilterChip
            active={filter === 'image'}
            onClick={() => setFilter('image')}
            label={t('studio.images')}
            Icon={ImageIcon}
          />
          <FilterChip
            active={filter === 'video'}
            onClick={() => setFilter('video')}
            label={t('studio.videos')}
            Icon={VideoIcon}
          />
        </div>
        {/* Brand filter — renders nothing until the agency has a brand. */}
        <div className="min-w-[200px]" data-testid="studio-brand-filter">
          <BrandSelect mode="filter" value={brandId} onChange={setBrandId} className="w-full" />
        </div>
        <div className="flex flex-wrap items-center gap-2">
        <Link href="/creatives" className="flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/10">
          <ScanLine className="h-4 w-4" />Анализ креативов
        </Link>
        <button
          onClick={() => setUploadOpen(true)}
          className="flex items-center gap-2 rounded-full bg-white/10 border border-white/15 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-white/15"
        >
          <UploadCloud className="h-4 w-4" />
          {t('studio.uploadVideo') || 'Загрузить видео'}
        </button>
        </div>
      </div>

      {uploadOpen && (
        <UploadVideoModal
          onClose={() => setUploadOpen(false)}
          onUploaded={() => {
            setUploadOpen(false);
            setRefreshKey((k) => k + 1);
          }}
        />
      )}

      {loading ? (
        <div className="columns-2 gap-3 sm:gap-4 md:columns-3 lg:columns-4 xl:columns-5">
          {Array.from({ length: 10 }).map((_, i) => {
            // Mix of common aspect ratios so the skeleton previews the
            // varied-shape masonry the real grid uses.
            const aspect = ['9 / 16', '16 / 9', '1 / 1', '4 / 3', '3 / 4'][i % 5];
            return (
              <div
                key={i}
                style={{ aspectRatio: aspect }}
                className="mb-4 break-inside-avoid animate-pulse border border-white/10 bg-white/[0.04]"
              />
            );
          })}
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-white/[0.04] py-16 text-center backdrop-blur-sm">
          <p className="text-sm font-semibold text-white">{t('studio.noGenerations')}</p>
          <p className="mt-2 text-xs text-white/60">{t('studio.noGenerationsHint')}</p>
        </div>
      ) : (
        <Grid items={items} />
      )}
    </section>

    {/* Bottom prompt — same generator as the Studio page, in compact mode
        (no hero heading). Wrapper stays transparent so the grid above
        breathes through; the card itself provides its own surface. */}
    <div className="shrink-0 px-3 pb-3 pt-2 sm:px-6 sm:pb-4">
      <div className="mx-auto w-full max-w-4xl">
        <HeroGenerator
          compact
          type={bottomType}
          onTypeChange={setBottomType}
          promptValue={bottomPrompt}
          onPromptChange={setBottomPrompt}
          onSubmitted={() => setRefreshKey((k) => k + 1)}
        />
      </div>
    </div>
    </div>
  );
}

function Grid({ items: initial }: { items: Item[] }) {
  const { t } = useI18n();
  const [items, setItems] = useState(initial);
  const [pendingDelete, setPendingDelete] = useState<Item | null>(null);
  const { authedFetch, getToken } = useAuthToken();

  // Keep local list in sync when parent fetch returns
  useEffect(() => setItems(initial), [initial]);

  const confirmDelete = async () => {
    if (!pendingDelete?.videoId) return;
    const target = pendingDelete;
    setPendingDelete(null);
    const res = await authedFetch(`/api/videos/${target.videoId}`, { method: 'DELETE' });
    if (!res.ok) return;
    setItems((prev) => prev.filter((x) => x.jobId !== target.jobId));
  };

  // Force a real file download (attachment) rather than opening the S3 URL in a
  // new tab — only for library videos (which have a videoId).
  const downloadItem = async (item: Item) => {
    if (!item.videoId) return;
    try {
      await downloadVideoFile(item.videoId, getToken());
    } catch (e) {
      console.error('download failed', e);
    }
  };

  return (
    <>
      {/* CSS columns (masonry) — items keep their native aspect ratio and
          flow without leaving gaps between mixed-ratio cards. */}
      <div className="columns-2 gap-3 sm:gap-4 md:columns-3 lg:columns-4 xl:columns-5">
        {items.map((item) => (
          <Card
            key={item.jobId}
            item={item}
            onDelete={() => setPendingDelete(item)}
            onDownload={() => downloadItem(item)}
          />
        ))}
      </div>
      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title={t('studio.deleteConfirmTitle') || 'Удалить видео?'}
        description={t('studio.deleteConfirm') || 'Удалить это видео без возможности восстановления?'}
        confirmLabel={t('studio.delete') || 'Удалить'}
        cancelLabel={t('common.cancel') || 'Отмена'}
        tone="destructive"
        variant="dark"
        onConfirm={confirmDelete}
      />
    </>
  );
}

function FilterChip({
  active,
  onClick,
  label,
  Icon,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  Icon?: any;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition ${
        active
          ? 'bg-white/15 text-white shadow-sm'
          : 'text-white/60 hover:bg-white/10 hover:text-white'
      }`}
    >
      {Icon && <Icon className="h-3.5 w-3.5" />}
      {label}
    </button>
  );
}

function Card({ item, onDelete, onDownload }: { item: Item; onDelete: () => void; onDownload: () => void }) {
  const { t } = useI18n();
  const output = item.s3Url || item.outputs?.[0];
  const isVideo = item.type === 'video';
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [muted, setMuted] = useState(true);
  // Measured aspect (videoWidth / naturalWidth … etc.). When set, it overrides
  // the metadata-based `item.aspectRatio` so the card always matches the
  // actual media shape exactly — even when the API returned no metadata or
  // returned a value out of sync with the encoded file.
  const [measuredAspect, setMeasuredAspect] = useState<string | null>(null);
  const aspectRatio = measuredAspect ?? parseAspectRatio(item.aspectRatio);

  // Постерная плитка не грузит метаданные видео — аспект берём из превью-картинки.
  useEffect(() => {
    if (!item.thumbnailUrl) return;
    const im = new window.Image();
    im.onload = () => {
      if (im.naturalWidth && im.naturalHeight) {
        setMeasuredAspect(`${im.naturalWidth} / ${im.naturalHeight}`);
      }
    };
    im.src = item.thumbnailUrl;
  }, [item.thumbnailUrl]);

  const toggleMute = (e: React.MouseEvent) => {
    e.stopPropagation();
    const v = videoRef.current;
    if (!v) return;
    const next = !v.muted;
    v.muted = next;
    if (!next) v.play().catch(() => {});
    setMuted(next);
  };
  const stop = (e: React.MouseEvent) => e.stopPropagation();

  // Play on hover (same UX as studio examples) — no autoplay so the grid is
  // calm at rest. Leaving the card pauses and rewinds to the first frame.
  const handleEnter = () => {
    const v = videoRef.current;
    if (!v) return;
    v.play().catch(() => {});
  };
  const handleLeave = () => {
    const v = videoRef.current;
    if (!v) return;
    v.pause();
    v.currentTime = 0;
  };

  return (
    <div
      data-testid="gallery-item"
      onMouseEnter={isVideo ? handleEnter : undefined}
      onMouseLeave={isVideo ? handleLeave : undefined}
      // `mb-4 break-inside-avoid` keeps each card whole inside the
      // CSS-columns masonry layout and reproduces the vertical gap.
      className="group relative mb-4 break-inside-avoid overflow-hidden border border-white/10 bg-white/[0.04] shadow-sm transition hover:-translate-y-0.5 hover:border-white/20 hover:bg-white/[0.06]"
    >
      <div
        style={{ aspectRatio }}
        className="relative flex items-center justify-center bg-white/[0.03]"
      >
        {item.status === 'completed' && output ? (
          isVideo ? (
            <video
              ref={videoRef}
              src={output}
              className="h-full w-full"
              controls={false}
              loop
              muted={muted}
              playsInline
              // С постером не тянем видео вовсе (30 плиток × metadata-фетч —
              // у не-faststart файлов это фактически весь mp4). Аспект в этом
              // случае меряется по самому постеру (эффект ниже).
              poster={item.thumbnailUrl || undefined}
              preload={item.thumbnailUrl ? 'none' : 'metadata'}
              onLoadedMetadata={(e) => {
                const v = e.currentTarget;
                if (v.videoWidth && v.videoHeight) {
                  setMeasuredAspect(`${v.videoWidth} / ${v.videoHeight}`);
                }
              }}
            />
          ) : (
            <SafeGenerationImage
              src={output}
              onMeasured={(w, h) => setMeasuredAspect(`${w} / ${h}`)}
            />
          )
        ) : item.status === 'failed' ? (
          <div className="p-4 text-center text-xs text-red-300">
            <p className="font-medium">{t('studio.failed')}</p>
            <p className="mt-1 line-clamp-3 text-red-200/80">{item.error || t('studio.generator.unknownError')}</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 px-3 text-center text-white/60">
            <Loader2 className="h-6 w-6 animate-spin" />
            <span className="text-xs">
              {item.status === 'created' ? t('studio.queued') : t('studio.generating')}
            </span>
          </div>
        )}

        {/* Type badge — small dark glass pill in the top-right corner,
            matching the studio examples grid style. Hover actions live in
            the top-left so they don't collide with it. */}
        <div className="pointer-events-none absolute right-2 top-2 rounded-md bg-black/55 px-2 py-0.5 text-[10px] font-semibold text-white ring-1 ring-white/15 backdrop-blur-md">
          {isVideo ? t('studio.video') : t('studio.image')}
        </div>

        {/* Mute toggle — same compact pill as the studio examples grid:
            hidden until hover, small dark glass background. */}
        {item.status === 'completed' && output && isVideo && (
          <button
            type="button"
            onClick={toggleMute}
            title={muted ? t('landing.examples.sound.unmute') : t('landing.examples.sound.mute')}
            className="absolute bottom-2 right-2 flex h-7 w-7 items-center justify-center rounded-full bg-black/40 text-white opacity-0 ring-1 ring-white/20 backdrop-blur-md transition hover:bg-black/55 group-hover:opacity-100"
          >
            {muted ? (
              <VolumeX className="h-3.5 w-3.5" />
            ) : (
              <Volume2 className="h-3.5 w-3.5" />
            )}
          </button>
        )}

        {/* Create campaign — bottom-left pill, appears on hover. Deeplinks
            into the new-campaign flow with the video pre-selected. */}
        {item.status === 'completed' && output && item.videoId && (
          <Link
            href={`/campaigns/new?videoId=${item.videoId}`}
            onClick={stop}
            title={t('studio.createCampaign') || 'Create campaign'}
            className="absolute bottom-2 left-2 flex h-7 items-center gap-1.5 rounded-full bg-brand-500 px-2.5 text-white opacity-0 shadow-[0_6px_22px_rgba(168,85,247,0.55)] ring-1 ring-white/15 transition hover:bg-brand-600 hover:shadow-[0_6px_26px_rgba(168,85,247,0.7)] group-hover:opacity-100"
          >
            <Megaphone className="h-3.5 w-3.5" />
            <span className="text-[11px] font-bold tracking-wide">
              {t('studio.createCampaign') || 'Create campaign'}
            </span>
          </Link>
        )}

        {/* Hover actions — top-left so the always-visible "Video" badge
            in the top-right stays unobstructed. */}
        {item.status === 'completed' && output && (
          <div className="absolute left-0 top-0 flex justify-start gap-1 p-2 opacity-0 transition group-hover:opacity-100">
            {isVideo && item.videoId && (
              <Link
                href={`/creatives?videoId=${encodeURIComponent(item.videoId)}`}
                onClick={stop}
                title="Анализ креатива"
                aria-label="Анализ креатива"
                className="rounded bg-black/60 p-1.5 text-white hover:bg-black/80"
              >
                <ScanLine className="h-3.5 w-3.5" />
              </Link>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(item.prompt);
              }}
              title={t('studio.copyPrompt')}
              className="p-1.5 rounded bg-black/60 hover:bg-black/80 text-white"
            >
              <Copy className="h-3.5 w-3.5" />
            </button>
            {!isVideo && (
              <a
                href={output}
                target="_blank"
                rel="noreferrer"
                onClick={stop}
                title={t('studio.open')}
                className="p-1.5 rounded bg-black/60 hover:bg-black/80 text-white"
              >
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
            {isVideo && item.videoId ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDownload();
                }}
                title={t('studio.download')}
                className="p-1.5 rounded bg-black/60 hover:bg-black/80 text-white"
              >
                <Download className="h-3.5 w-3.5" />
              </button>
            ) : (
              <a
                href={output}
                target="_blank"
                rel="noopener noreferrer"
                onClick={stop}
                title={t('studio.download')}
                className="p-1.5 rounded bg-black/60 hover:bg-black/80 text-white"
              >
                <Download className="h-3.5 w-3.5" />
              </a>
            )}
            {item.videoId && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
                title={t('studio.delete') || 'Удалить'}
                className="p-1.5 rounded bg-black/60 hover:bg-red-500/80 text-white"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Meta({
  label,
  value,
  variant = 'dark',
}: {
  label: string;
  value: string;
  variant?: 'light' | 'dark';
}) {
  return (
    <div
      className={
        variant === 'light'
          ? 'rounded-xl bg-ink-50 px-2 py-2'
          : 'rounded-xl bg-white/[0.05] px-2 py-2'
      }
    >
      <p
        className={
          variant === 'light'
            ? 'text-[10px] uppercase tracking-wide text-ink-500'
            : 'text-[10px] uppercase tracking-wide text-white/50'
        }
      >
        {label}
      </p>
      <p
        className={
          variant === 'light'
            ? 'mt-0.5 text-sm font-semibold text-ink-950'
            : 'mt-0.5 text-sm font-semibold text-white'
        }
      >
        {value}
      </p>
    </div>
  );
}

export function UploadVideoModal({
  onClose,
  onUploaded,
  variant = 'dark',
}: {
  onClose: () => void;
  onUploaded: () => void;
  /** Visual theme. `dark` (default) matches the Studio surfaces; `light`
   *  matches the campaigns / dashboard chrome. */
  variant?: 'light' | 'dark';
}) {
  const isLight = variant === 'light';
  const { t } = useI18n();
  const { authedFetch } = useAuthToken();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [metadata, setMetadata] = useState<{
    duration: number;
    width: number;
    height: number;
    aspectRatio: string;
  } | null>(null);
  const [title, setTitle] = useState('');
  // Agency's brand label for the media library (optional; '' = none).
  const [brandId, setBrandId] = useState('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  // Hold the in-flight S3 PUT so the user can abort a long upload. `canceled`
  // lets the async chain bail quietly if it wins the race against abort().
  const xhrRef = useRef<XMLHttpRequest | null>(null);
  const canceledRef = useRef(false);

  const pickFile = async (selected?: File) => {
    if (!selected) return;
    setError('');
    const validation = validateVideoFile(selected);
    if (!validation.valid) {
      setError(validation.error || 'Неподдерживаемый файл');
      return;
    }
    setFile(selected);
    setTitle(selected.name.replace(/\.[^/.]+$/, ''));
    setPreview(URL.createObjectURL(selected));
    // Превью в браузере — необязательно: Chrome не декодирует HEVC
    // (iPhone-.mov), а сервер замеряет и перекодирует файл сам.
    try {
      setMetadata(await extractVideoMetadata(selected));
    } catch {
      setMetadata(null);
    }
  };

  // Abort an in-flight upload (or just close when idle). The XHR abort rejects
  // the upload promise; the chain sees `canceled` and returns without an error.
  const cancelUpload = () => {
    if (!uploading) {
      onClose();
      return;
    }
    canceledRef.current = true;
    xhrRef.current?.abort();
    xhrRef.current = null;
    setUploading(false);
    setProgress(0);
  };

  const handleUpload = async () => {
    if (!file || !title.trim()) return;
    canceledRef.current = false;
    setUploading(true);
    setError('');
    setProgress(0);
    try {
      // 1. Presigned S3 URL + Video record
      const presignRes = await authedFetch('/api/videos/upload/presign', {
        method: 'POST',
        body: JSON.stringify({
          fileName: file.name,
          fileType: videoMimeType(file),
          fileSize: file.size,
          ...(brandId ? { brandId } : {}),
        }),
      });
      if (!presignRes.ok) throw new Error('Не удалось получить ссылку загрузки');
      if (canceledRef.current) return;
      const { uploadUrl, videoId, uploadHeaders } = await presignRes.json();

      // 2. Upload the file straight to S3 with progress
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhrRef.current = xhr;
        xhr.addEventListener('abort', () =>
          reject(new DOMException('Загрузка отменена', 'AbortError'))
        );
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            setProgress(Math.round((e.loaded / e.total) * 90));
          }
        });
        xhr.addEventListener('load', () =>
          // S3 answers a successful PUT with 200, Azure Blob with 201 — accept any 2xx.
          xhr.status >= 200 && xhr.status < 300
            ? resolve()
            : reject(new Error('Ошибка загрузки в хранилище'))
        );
        xhr.addEventListener('error', () =>
          reject(new Error('Ошибка загрузки в хранилище'))
        );
        xhr.open('PUT', uploadUrl);
        // Бэкенд возвращает точный набор заголовков под провайдера
        // (S3: Content-Type/Cache-Control; Azure: x-ms-blob-type и x-ms-*).
        if (uploadHeaders && Object.keys(uploadHeaders).length > 0) {
          for (const [h, v] of Object.entries(uploadHeaders)) xhr.setRequestHeader(h, v as string);
        } else {
          xhr.setRequestHeader('Content-Type', file.type);
        // Match the CacheControl baked into the presigned URL so S3 actually
        // stores it on the object. Without this the kiosk re-downloads the
        // file on every page refresh.
          xhr.setRequestHeader('Cache-Control', 'public, max-age=31536000, immutable');
        }
        xhr.send(file);
      });
      xhrRef.current = null;
      if (canceledRef.current) return;

      // 3. Finalize — регистрируем завершение, видео сразу готово
      const completeRes = await authedFetch('/api/videos/upload/complete', {
        method: 'POST',
        body: JSON.stringify({
          videoId,
          title: title.trim(),
          metadata: {
            ...metadata,
            fileSize: file.size,
            format: videoMimeType(file).split('/')[1],
          },
        }),
      });
      if (!completeRes.ok) {
        // The ingest gate answers with a concrete reason (wrong codec, too
        // short, under 1080p) — showing "upload failed" instead would leave the
        // advertiser with no idea what to fix.
        const reason = await completeRes.json().catch(() => null);
        throw new Error(reason?.error || 'Не удалось завершить загрузку');
      }
      setProgress(100);
      onUploaded();
    } catch (err: any) {
      xhrRef.current = null;
      // User-initiated abort: state was already reset in cancelUpload, so stay quiet.
      if (canceledRef.current || err?.name === 'AbortError') {
        setUploading(false);
        setProgress(0);
        return;
      }
      setError(err?.message || 'Ошибка загрузки');
      setUploading(false);
    }
  };

  const overlayClass = isLight
    ? 'fixed inset-0 z-50 flex items-center justify-center bg-ink-950/40 p-4 backdrop-blur-sm'
    : 'fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm';
  const cardClass = isLight
    ? 'flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-ink-950/10 bg-white text-ink-950 shadow-[0_30px_80px_rgba(7,5,12,0.25)]'
    : 'flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#15091f]/95 text-white shadow-2xl backdrop-blur-xl';
  const headerBorder = isLight ? 'border-b border-ink-950/10' : 'border-b border-white/10';
  const headerTitle = isLight ? 'text-lg font-semibold text-ink-950' : 'text-lg font-semibold text-white';
  const closeBtn = isLight
    ? 'rounded-full p-1.5 text-ink-500 transition hover:bg-ink-50 hover:text-ink-950 disabled:opacity-40'
    : 'rounded-full p-1.5 text-white/60 transition hover:bg-white/10 hover:text-white disabled:opacity-40';
  const dropZone = isLight
    ? 'flex cursor-pointer flex-col items-center gap-3 rounded-2xl border-2 border-dashed border-ink-950/15 bg-ink-50 px-6 py-12 text-center transition hover:border-brand-500/40 hover:bg-brand-50/40'
    : 'flex cursor-pointer flex-col items-center gap-3 rounded-2xl border-2 border-dashed border-white/15 bg-white/[0.04] px-6 py-12 text-center transition hover:border-white/30 hover:bg-white/[0.08]';
  const dropIconWrap = isLight
    ? 'flex h-14 w-14 items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-ink-950/10'
    : 'flex h-14 w-14 items-center justify-center rounded-full bg-white/10';
  const dropIcon = isLight ? 'h-7 w-7 text-ink-500' : 'h-7 w-7 text-white/70';
  const dropTitle = isLight ? 'text-sm font-semibold text-ink-950' : 'text-sm font-semibold text-white';
  const dropHint = isLight ? 'mt-1 text-xs text-ink-500' : 'mt-1 text-xs text-white/50';
  const previewBg = isLight ? 'aspect-video overflow-hidden rounded-2xl bg-ink-100' : 'aspect-video overflow-hidden rounded-2xl bg-black';
  const inputLabel = isLight ? 'mb-1.5 block text-xs font-semibold text-ink-500' : 'mb-1.5 block text-xs font-semibold text-white/60';
  const inputField = isLight
    ? 'w-full rounded-xl border border-ink-950/15 bg-white px-3 py-2 text-sm text-ink-950 outline-none transition placeholder:text-ink-400 focus:border-brand-500/60 focus:ring-2 focus:ring-brand-500/15 disabled:opacity-50'
    : 'w-full rounded-xl border border-white/15 bg-white/10 px-3 py-2 text-sm text-white outline-none transition placeholder:text-white/40 focus:border-fuchsia-400/60 focus:ring-2 focus:ring-fuchsia-500/20 disabled:opacity-50';
  const progressLabel = isLight ? 'flex justify-between text-xs font-medium text-ink-500' : 'flex justify-between text-xs font-medium text-white/60';
  const progressTrack = isLight ? 'h-2 overflow-hidden rounded-full bg-ink-100' : 'h-2 overflow-hidden rounded-full bg-white/10';
  const progressBar = isLight ? 'h-full bg-brand-500 transition-all duration-300' : 'h-full bg-fuchsia-500 transition-all duration-300';
  const chooseDifferent = isLight
    ? 'text-xs font-medium text-ink-500 underline-offset-2 transition hover:text-ink-950 hover:underline'
    : 'text-xs font-medium text-white/50 underline-offset-2 transition hover:text-white hover:underline';
  const errorClass = isLight
    ? 'rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700'
    : 'rounded-xl border border-red-400/30 bg-red-500/10 px-3 py-2 text-xs text-red-200';
  const footerBorder = isLight ? 'border-t border-ink-950/10' : 'border-t border-white/10';
  const cancelBtn = isLight
    ? 'rounded-full px-4 py-2 text-sm font-semibold text-ink-600 transition hover:bg-ink-50 hover:text-ink-950 disabled:opacity-40'
    : 'rounded-full px-4 py-2 text-sm font-semibold text-white/60 transition hover:bg-white/10 hover:text-white disabled:opacity-40';

  return (
    <div
      className={overlayClass}
      onClick={uploading ? undefined : onClose}
    >
      <div
        className={cardClass}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`flex shrink-0 items-center justify-between px-6 py-4 ${headerBorder}`}>
          <h2 className={headerTitle}>
            {t('studio.uploadVideo') || 'Загрузить видео'}
          </h2>
          <button
            onClick={cancelUpload}
            className={closeBtn}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-6">
          {!file ? (
            <label className={dropZone}>
              <div className={dropIconWrap}>
                <UploadCloud className={dropIcon} />
              </div>
              <div>
                <p className={dropTitle}>
                  {t('studio.uploadPick') || 'Выберите видеофайл'}
                </p>
                <p className={dropHint}>MP4, MOV, MKV, WebM · до 500 МБ</p>
              </div>
              <input
                type="file"
                accept={VIDEO_ACCEPT}
                className="hidden"
                onChange={(e) => pickFile(e.target.files?.[0])}
              />
            </label>
          ) : (
            <>
              <div className={previewBg}>
                {preview && (
                  <video src={preview} controls className="h-full w-full object-contain" />
                )}
              </div>

              {metadata && (
                <div className="grid grid-cols-3 gap-2 text-center">
                  <Meta
                    variant={variant}
                    label={t('videos.duration') || 'Длительность'}
                    value={`${metadata.duration} с`}
                  />
                  <Meta
                    variant={variant}
                    label={t('videos.resolution') || 'Разрешение'}
                    value={`${metadata.width}×${metadata.height}`}
                  />
                  <Meta
                    variant={variant}
                    label={t('videos.aspectRatio') || 'Формат'}
                    value={metadata.aspectRatio}
                  />
                </div>
              )}

              <div>
                <label className={inputLabel}>
                  {t('videos.titleLabel') || 'Название'}
                </label>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  disabled={uploading}
                  className={inputField}
                />
              </div>

              {/* Brand label — BrandSelect renders nothing without brands. */}
              <BrandSelect mode="label" value={brandId} onChange={setBrandId} disabled={uploading} className="w-full" />

              {uploading && (
                <div className="space-y-1.5">
                  <div className={progressLabel}>
                    <span>{t('videos.uploading') || 'Загрузка...'}</span>
                    <span>{progress}%</span>
                  </div>
                  <div className={progressTrack}>
                    <div
                      className={progressBar}
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              )}

              {!uploading && (
                <button
                  onClick={() => {
                    setFile(null);
                    setPreview(null);
                    setMetadata(null);
                    setError('');
                  }}
                  className={chooseDifferent}
                >
                  {t('videos.chooseDifferent') || 'Выбрать другой файл'}
                </button>
              )}
            </>
          )}

          {error && <div className={errorClass}>{error}</div>}
        </div>

        <div
          className={`flex shrink-0 justify-end gap-2 px-6 py-4 ${footerBorder} ${
            isLight ? 'bg-white' : 'bg-[#15091f]'
          }`}
        >
          <button
            onClick={cancelUpload}
            className={cancelBtn}
          >
            {uploading
              ? t('studio.cancelUpload') || 'Отменить загрузку'
              : t('common.cancel') || 'Отмена'}
          </button>
          <button
            onClick={handleUpload}
            disabled={!file || !title.trim() || uploading}
            className="flex items-center gap-2 rounded-full bg-brand-500 px-5 py-2 text-sm font-semibold text-white transition hover:bg-brand-600 disabled:opacity-40"
          >
            {uploading && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('studio.uploadSubmit') || 'Загрузить'}
          </button>
        </div>
      </div>
    </div>
  );
}

